import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getActivityKey } from "../../utils/checkout";
import { Box, MenuItem, Select, Typography, Checkbox, FormControlLabel } from "@mui/material";
import BpRadio from "./BpRadio";
import IncrementalButtons from "../IncrementalButtons";

const CmLabel = (props) => {
    const { children, ...lProps } = props;
    return (
        <label style={{ color: '#6D7280', fontSize: '14px' }} {...lProps}>{children}</label>
    );
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

/** ───────────────── Normalización de claves (IDs) ───────────────── **/
// Use shared getActivityKey to ensure the same normalization across the app
// Alias local name for clarity in this component
const norm = (v) => String(v ?? '');
const getAttractionKey = (a) => {
    if (a == null) return '';
    if (typeof a === 'string' || typeof a === 'number') return norm(a);
    return getActivityKey(a);
};
const getAttractionQuantity = (a, fallback = 1) => {
    const ratesMap = (a && typeof a === 'object' && a.rate_quantities && typeof a.rate_quantities === 'object')
        ? a.rate_quantities
        : null;
    if (ratesMap) {
        const sumFromRates = Object.values(ratesMap).reduce((sum, n) => sum + (Number(n) || 0), 0);
        if (sumFromRates >= 0) return Math.floor(sumFromRates);
    }
    if (!a || typeof a !== 'object') return fallback;
    const raw = Number(a.quantity ?? a.value);
    if (!Number.isFinite(raw) || raw <= 0) return fallback;
    return Math.floor(raw);
};
const toRateKey = (rate, idx = 0) => {
    const pax = rate?.pax;
    if (Array.isArray(pax)) return String(pax.join(','));
    if (pax != null) return String(pax);
    return `rate_${idx}`;
};
const getRateMaxQty = (rate, persons = {}) => {
    const paxRaw = rate?.pax;
    const paxCandidates = Array.isArray(paxRaw) ? paxRaw.map(String) : [String(paxRaw ?? '')];
    let max = 0;
    for (const [paxType, paxCount] of Object.entries(persons || {})) {
        const match = paxCandidates.some((candidate) => {
            const c = String(candidate || '').toLowerCase();
            const p = String(paxType || '').toLowerCase();
            return c === p || c.includes(p) || p.includes(c);
        });
        if (match) max += Math.max(0, Number(paxCount) || 0);
    }
    return Math.max(0, Math.floor(max));
};
const getRateInitialQty = (rate, persons = {}, maxQuantity = 1) => {
    const hardMax = Math.max(0, Math.min(maxQuantity, getRateMaxQty(rate, persons)));
    if (hardMax <= 0) return 0;
    const paxRaw = rate?.pax;
    const paxCandidates = Array.isArray(paxRaw) ? paxRaw.map(String) : [String(paxRaw ?? '')];
    for (const [paxType, paxCount] of Object.entries(persons || {})) {
        const match = paxCandidates.some((candidate) => {
            const c = String(candidate || '').toLowerCase();
            const p = String(paxType || '').toLowerCase();
            return c === p || c.includes(p) || p.includes(c);
        });
        if (match) {
            const n = Number(paxCount) || 0;
            return Math.max(0, Math.min(hardMax, n));
        }
    }
    return hardMax;
};
const rateMatchesSelectedPax = (rate, selectedPaxTypes = []) => {
    if (!Array.isArray(selectedPaxTypes) || selectedPaxTypes.length === 0) return true;
    const paxRaw = rate?.pax;
    const ratePaxList = Array.isArray(paxRaw) ? paxRaw.map(String) : [String(paxRaw ?? '')];
    return selectedPaxTypes.some((selectedPax) => {
        const s = String(selectedPax || '').toLowerCase();
        return ratePaxList.some((rp) => {
            const r = String(rp || '').toLowerCase();
            return r === s || r.includes(s) || s.includes(r);
        });
    });
};

const Step2 = ({
    extrasGuide = [],
    extrasVehicle = [],
    extrasAttractions = [],
    initialGuide = null,
    persons = {},
    initialVehicle = null,
    initialAttractions = [],
    onSelectionChange = (..._args) => { },
    modalOpen = false,
    guideIncluded = undefined,
    initialVehicleOptionChecked = false,
    currencySymbol = '',
    currencyCode = ''
}) => {
    const { t } = useTranslation("checkout");
    const resolvedCurrencySymbol = useMemo(() => {
        const directSymbol = String(currencySymbol ?? '').trim();
        if (directSymbol) return directSymbol;

        const code = String(currencyCode ?? '').trim().toUpperCase();
        if (!code) return '$';

        try {
            const parts = new Intl.NumberFormat('en', {
                style: 'currency',
                currency: code,
                currencyDisplay: 'narrowSymbol'
            }).formatToParts(0);
            const symbol = parts.find((part) => part.type === 'currency')?.value;
            return symbol || code;
        } catch (_err) {
            return code;
        }
    }, [currencySymbol, currencyCode]);
    // ─────────── Guide options
    const hasGuideExtras = Array.isArray(extrasGuide) && extrasGuide.length > 0;
    const guideOptions = useMemo(() => {
        if (!hasGuideExtras) return [];
        return extrasGuide.map(g => {
            if (!g) return null;
            if (typeof g === 'string' || typeof g === 'number') {
                const id = norm(g);
                return { id, type_guide: id, price: 0 };
            }
            const stableId = g.id ?? g.type_guide ?? g.type ?? g.name ?? String(g.title ?? g.label ?? 'guide');
            return {
                id: norm(stableId),
                type_guide: g.type_guide ?? g.type ?? g.name ?? String(g.id ?? stableId),
                price: g.price ?? 0,
                excepcions: g.excepcions ?? []
            };
        }).filter(Boolean);
    }, [extrasGuide, hasGuideExtras]);

    console.log('Guide options:', guideOptions);

    // ─────────── Vehicle options
    const hasVehicleExtras = Array.isArray(extrasVehicle) && extrasVehicle.length > 0;
    const vehicleOptions = useMemo(() => {
        if (!hasVehicleExtras) return [];
        return extrasVehicle.map(v => {
            if (!v) return null;
            if (typeof v === 'string' || typeof v === 'number') {
                const id = norm(v);
                return { id, vehicle_type: id, price: 0 };
            }
            const stableId = v.id ?? v.vehicle_type ?? v.name ?? String(v.title ?? v.label ?? 'vehicle');
            return {
                id: norm(stableId),
                vehicle_type: v.vehicle_type ?? v.name ?? String(v.id ?? stableId),
                price: v.price ?? 0,
                vehicle_option: v.vehicle_option ?? null,
                excepcions: v.excepcions ?? []
            };
        }).filter(Boolean);
    }, [extrasVehicle, hasVehicleExtras]);

    // ─────────── State
    const [vehicleKey, setVehicleKey] = useState('');
    const [guideKey, setGuideKey] = useState('');
    const [vehicleOptionChecked, setVehicleOptionChecked] = useState(() => Boolean(initialVehicleOptionChecked));
    const paxCount = useMemo(
        () => Object.values(persons).reduce((s, v) => s + (Number(v) || 0), 0) || 1,
        [persons]
    );
    const maxQuantity = Math.max(1, Number(paxCount) || 1);
    const selectedPaxTypes = useMemo(
        () => Object.entries(persons || {})
            .filter(([, value]) => (Number(value) || 0) > 0)
            .map(([pax]) => String(pax)),
        [persons]
    );
    const getRateMaxByKeys = (attractionKey, rateKey) => {
        const attraction = (Array.isArray(extrasAttractions) ? extrasAttractions : []).find(a => getAttractionKey(a) === attractionKey);
        const rates = Array.isArray(attraction?.rates) ? attraction.rates : [];
        const visibleRates = rates.filter((rate) => rateMatchesSelectedPax(rate, selectedPaxTypes));
        const rate = visibleRates.find((r, idx) => toRateKey(r, idx) === rateKey) || null;
        if (!rate) return 0;
        return getRateMaxQty(rate, persons);
    };

    // selectedAttractions guarda SIEMPRE keys string
    const [selectedAttractions, setSelectedAttractions] = useState(() => {
        if (!Array.isArray(initialAttractions)) return [];
        return initialAttractions.map(a => getAttractionKey(a));
    });
    const [attractionRateQuantities, setAttractionRateQuantities] = useState(() => {
        if (!Array.isArray(initialAttractions)) return {};
        const next = {};
        initialAttractions.forEach((a) => {
            const key = getAttractionKey(a);
            if (!key) return;
            const rates = Array.isArray(a?.rates) ? a.rates : [];
            const filteredRates = rates.filter((rate) => rateMatchesSelectedPax(rate, selectedPaxTypes));
            const savedRates = (a && typeof a === 'object' && a.rate_quantities && typeof a.rate_quantities === 'object')
                ? a.rate_quantities
                : {};
            if (filteredRates.length > 0) {
                const rq = {};
                filteredRates.forEach((rate, idx) => {
                    const rateKey = toRateKey(rate, idx);
                    const saved = Number(savedRates?.[rateKey]);
                    rq[rateKey] = Number.isFinite(saved)
                        ? Math.max(0, Math.min(getRateMaxQty(rate, persons), Math.floor(saved)))
                        : getRateInitialQty(rate, persons, maxQuantity);
                });
                next[key] = rq;
            } else {
                next[key] = { default: Math.max(0, Math.min(maxQuantity, getAttractionQuantity(a, maxQuantity))) };
            }
        });
        return next;
    });

    const attractionsInitRef = useRef(false);

    // ─────────── Handlers
    const handleChangeVehicle = (event) => setVehicleKey(event.target.value);
    const handleChangeGuide = (event) => setGuideKey(event.target.value);
    const handleToggleVehicleOption = () => setVehicleOptionChecked(prev => !prev);

    const toggleAttraction = (key) => {
        setSelectedAttractions(prev => {
            const exists = prev.includes(key);
            if (exists) {
                setAttractionRateQuantities((prevQty) => {
                    if (!(key in prevQty)) return prevQty;
                    const { [key]: _drop, ...rest } = prevQty;
                    return rest;
                });
                return prev.filter(n => n !== key);
            }
            const attraction = (Array.isArray(extrasAttractions) ? extrasAttractions : []).find(a => getAttractionKey(a) === key);
            const rates = Array.isArray(attraction?.rates) ? attraction.rates : [];
            const filteredRates = rates.filter((rate) => rateMatchesSelectedPax(rate, selectedPaxTypes));
            setAttractionRateQuantities((prevQty) => {
                if (prevQty?.[key]) return prevQty;
                const rq = {};
                if (filteredRates.length > 0) {
                    filteredRates.forEach((rate, idx) => {
                        const rateKey = toRateKey(rate, idx);
                        rq[rateKey] = getRateInitialQty(rate, persons, maxQuantity);
                    });
                } else {
                    rq.default = maxQuantity;
                }
                return { ...prevQty, [key]: rq };
            });
            return [...prev, key];
        });
    };
    const handleRateIncrement = (token) => {
        const [attractionKey, rateKey] = String(token || '').split('::');
        if (!attractionKey || !rateKey) return;
        const rateMax = getRateMaxByKeys(attractionKey, rateKey);
        setAttractionRateQuantities((prev) => {
            const current = Number(prev?.[attractionKey]?.[rateKey]) || 0;
            if (current >= rateMax) return prev;
            return {
                ...prev,
                [attractionKey]: {
                    ...(prev?.[attractionKey] || {}),
                    [rateKey]: current + 1
                }
            };
        });
    };
    const handleRateDecrement = (token) => {
        const [attractionKey, rateKey] = String(token || '').split('::');
        if (!attractionKey || !rateKey) return;
        let shouldUncheckAttraction = false;
        setAttractionRateQuantities((prev) => {
            const current = Number(prev?.[attractionKey]?.[rateKey]) || 0;
            if (current <= 0) return prev;
            const nextValue = current - 1;
            const nextAttractionRates = {
                ...(prev?.[attractionKey] || {}),
                [rateKey]: nextValue
            };
            const allRatesZero = Object.values(nextAttractionRates).every((n) => (Number(n) || 0) <= 0);
            if (allRatesZero) {
                shouldUncheckAttraction = true;
                const { [attractionKey]: _drop, ...restAttractions } = prev || {};
                return restAttractions;
            }
            return {
                ...prev,
                [attractionKey]: nextAttractionRates
            };
        });
        if (shouldUncheckAttraction) {
            setSelectedAttractions((prev) => prev.filter((k) => k !== attractionKey));
        }
    };

    // ─────────── Init Guide
    useEffect(() => {
        if (!modalOpen) return;

        if (initialGuide && (typeof initialGuide === 'string' || typeof initialGuide === 'number' || initialGuide.id || initialGuide.type_guide)) {
            const id = typeof initialGuide === 'string' || typeof initialGuide === 'number'
                ? norm(initialGuide)
                : norm(initialGuide.id ?? initialGuide.type_guide);
            setGuideKey(id);
            return;
        }
        if (!hasGuideExtras) {
            setGuideKey('');
            return;
        }
        const freeGuide = guideOptions.find(g => Number(g.price) === 0) || guideOptions[0];
        setGuideKey(norm(freeGuide?.id ?? ''));
    }, [modalOpen, initialGuide, guideOptions, hasGuideExtras, guideIncluded]);

    // ─────────── Init Vehicle
    useEffect(() => {
        if (!modalOpen) return;

        if (vehicleOptions.length === 1 && String(vehicleOptions[0].id) === '0') {
            setVehicleKey('');
            return;
        }
        if (initialVehicle && (typeof initialVehicle === 'string' || typeof initialVehicle === 'number' || initialVehicle.id || initialVehicle.vehicle_type)) {
            const id = typeof initialVehicle === 'string' || typeof initialVehicle === 'number'
                ? norm(initialVehicle)
                : norm(initialVehicle.id ?? initialVehicle.vehicle_type);
            setVehicleKey(id);
            return;
        }
        if (!hasVehicleExtras) {
            setVehicleKey('');
            return;
        }
        const freeOption = vehicleOptions.find(v => Number(v.price) === 0) || vehicleOptions[0];
        if (freeOption) setVehicleKey(norm(freeOption.id));
    }, [modalOpen, initialVehicle, vehicleOptions, hasVehicleExtras]);

    // ─────────── Vehicle option checkbox initial
    useEffect(() => {
        const selectedVehicle = vehicleOptions.find(v => v.id === vehicleKey) || null;
        if (!selectedVehicle || !('vehicle_option' in selectedVehicle) || !selectedVehicle.vehicle_option) {
            setVehicleOptionChecked(Boolean(initialVehicleOptionChecked && (!selectedVehicle || !selectedVehicle.vehicle_option)) || false);
            return;
        }
        const vo = selectedVehicle.vehicle_option;
        const first = Array.isArray(vo) ? vo[0] : vo;
        const val = first?.value ?? 0;

        if (modalOpen && typeof initialVehicleOptionChecked !== 'undefined') {
            setVehicleOptionChecked(Boolean(initialVehicleOptionChecked));
        } else {
            setVehicleOptionChecked(Number(val) > 0);
        }
    }, [vehicleKey, vehicleOptions, modalOpen, initialVehicleOptionChecked]);

    // ─────────── Emitir cambios al padre
    useEffect(() => {
        const selectedGuide = guideOptions.find(g => g.id === guideKey) || null;
        const selectedVehicle = vehicleOptions.find(v => v.id === vehicleKey) || null;

        // Mapear selectedAttractions (keys) a objetos completos cuando existan
        const available = Array.isArray(extrasAttractions) ? extrasAttractions : [];
        const selectedAttractionsObjs = available
            .filter(a => selectedAttractions.includes(getAttractionKey(a)))
            .map(a => {
                const key = getAttractionKey(a);
                const rates = Array.isArray(a?.rates) ? a.rates : [];
                const filteredRates = rates.filter((rate) => rateMatchesSelectedPax(rate, selectedPaxTypes));
                const rateQuantities = { ...(attractionRateQuantities?.[key] || {}) };
                if (filteredRates.length > 0) {
                    filteredRates.forEach((rate, idx) => {
                        const rateKey = toRateKey(rate, idx);
                        const current = Number(rateQuantities?.[rateKey]);
                        const rateMax = getRateMaxQty(rate, persons);
                        if (!Number.isFinite(current)) {
                            rateQuantities[rateKey] = getRateInitialQty(rate, persons, maxQuantity);
                        } else {
                            rateQuantities[rateKey] = Math.max(0, Math.min(rateMax, Math.floor(current)));
                        }
                    });
                }
                const quantityFromRates = Object.values(rateQuantities).reduce((sum, n) => sum + (Number(n) || 0), 0);
                return {
                    ...a,
                    rate_quantities: rateQuantities,
                    quantity: Math.max(0, Math.min(maxQuantity, quantityFromRates))
                };
            });

        let vehicleWithOption = null;
        if (selectedVehicle) {
            vehicleWithOption = { ...selectedVehicle };
            const voVal = selectedVehicle?.vehicle_option;
            if (voVal) {
                const voList = Array.isArray(voVal) ? voVal : [voVal];
                const firstVo = voList[0] || null;
                if (firstVo) {
                    vehicleWithOption.vehicle_option = {
                        ...firstVo,
                        value: vehicleOptionChecked ? (Number(firstVo?.value ?? 1) || 1) : 0
                    };
                }
            }
        }

        onSelectionChange({
            guide: selectedGuide,
            vehicle: vehicleWithOption,
            attractions: selectedAttractionsObjs,   // objetos completos cuando es posible
            vehicleOptionChecked
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guideKey, guideOptions, vehicleKey, vehicleOptions, vehicleOptionChecked, selectedAttractions, extrasAttractions, attractionRateQuantities, maxQuantity, persons, selectedPaxTypes]);

    // ─────────── Inicializar attractions desde props
    // Detecta flanco de subida del modal y solo inicializa una vez por apertura
    const lastModalOpenRef = useRef(false);

    useEffect(() => {
        const justOpened = modalOpen && !lastModalOpenRef.current;
        lastModalOpenRef.current = modalOpen;

        if (!justOpened || attractionsInitRef.current) return;

        // 1) Si hay initialAttractions, úsalos
        if (Array.isArray(initialAttractions) && initialAttractions.length > 0) {
            setSelectedAttractions(initialAttractions.map(a => getAttractionKey(a)));
            const quantities = {};
            initialAttractions.forEach((a) => {
                const key = getAttractionKey(a);
                if (!key) return;
                const rates = Array.isArray(a?.rates) ? a.rates : [];
                const filteredRates = rates.filter((rate) => rateMatchesSelectedPax(rate, selectedPaxTypes));
                const savedRates = (a && typeof a === 'object' && a.rate_quantities && typeof a.rate_quantities === 'object')
                    ? a.rate_quantities
                    : {};
                if (filteredRates.length > 0) {
                    const rq = {};
                    filteredRates.forEach((rate, idx) => {
                        const rateKey = toRateKey(rate, idx);
                        const saved = Number(savedRates?.[rateKey]);
                        rq[rateKey] = Number.isFinite(saved)
                            ? Math.max(0, Math.min(getRateMaxQty(rate, persons), Math.floor(saved)))
                            : getRateInitialQty(rate, persons, maxQuantity);
                    });
                    quantities[key] = rq;
                } else {
                    quantities[key] = { default: Math.max(0, Math.min(maxQuantity, getAttractionQuantity(a, maxQuantity))) };
                }
            });
            setAttractionRateQuantities(quantities);
            attractionsInitRef.current = true;
            return;
        }

        // 2) Si NO hay initialAttractions:
        //    - Si extrasAttractions está vacío, NO limpies, deja lo que tenga el usuario
        //    - Si extrasAttractions tiene datos pero no hay selección inicial, también NO toques nada
        attractionsInitRef.current = true;
    }, [modalOpen, initialAttractions, maxQuantity, persons, selectedPaxTypes]); // ← solo depende de modalOpen

    // Al cerrar el modal, permite re-inicializar en la próxima apertura
    useEffect(() => {
        if (!modalOpen) {
            attractionsInitRef.current = false;
        }
    }, [modalOpen]);
    useEffect(() => {
        setAttractionRateQuantities((prev) => {
            if (!selectedAttractions.length) return {};
            let changed = false;
            const next = {};
            selectedAttractions.forEach((attractionKey) => {
                const attraction = (Array.isArray(extrasAttractions) ? extrasAttractions : []).find(a => getAttractionKey(a) === attractionKey);
                const rates = Array.isArray(attraction?.rates) ? attraction.rates : [];
                const filteredRates = rates.filter((rate) => rateMatchesSelectedPax(rate, selectedPaxTypes));
                const prevRates = prev?.[attractionKey] || {};
                const nextRates = {};
                if (filteredRates.length > 0) {
                    filteredRates.forEach((rate, idx) => {
                        const rk = toRateKey(rate, idx);
                        const current = Number(prevRates?.[rk]);
                        const rateMax = getRateMaxQty(rate, persons);
                        const fallback = getRateInitialQty(rate, persons, maxQuantity);
                        const clamped = Number.isFinite(current)
                            ? Math.max(0, Math.min(rateMax, Math.floor(current)))
                            : fallback;
                        nextRates[rk] = clamped;
                        if (clamped !== current) changed = true;
                    });
                } else {
                    const current = Number(prevRates?.default);
                    const fallback = Math.max(0, Math.min(maxQuantity, maxQuantity));
                    const clamped = Number.isFinite(current)
                        ? Math.max(0, Math.min(maxQuantity, Math.floor(current)))
                        : fallback;
                    nextRates.default = clamped;
                    if (clamped !== current) changed = true;
                }
                next[attractionKey] = nextRates;
            });
            if (!changed && Object.keys(prev || {}).length === Object.keys(next).length) return prev;
            return next;
        });
    }, [maxQuantity, selectedAttractions, extrasAttractions, persons, selectedPaxTypes]);


    // ─────────── Render
    return (
        <>
            <Typography variant="h5" sx={{ fontSize: '20px', color: 'secondary.main' }}>
                {t('update-tour-step2-title')}
            </Typography>

            {(Number(guideIncluded) !== 0 || guideOptions.length > 1) && (
                <Box sx={{ display: 'grid', width: '100%', py: '5px' }}>
                    <CmLabel htmlFor="guideType">{t('update-tour-step2-guide-question')}</CmLabel>
                    <Select
                        labelId="guide-type"
                        name="guideType"
                        value={guideKey ? guideKey : (hasGuideExtras ? String(guideOptions[0]?.id ?? '') : '')}
                        onChange={handleChangeGuide}
                        disabled={!hasGuideExtras}
                        MenuProps={MenuProps}
                        sx={{ fontSize: '13px', height: '40px', borderRadius: '8px' }}
                    >
                        {guideOptions.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.type_guide} {item.price ? `- $${item.price}` : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {(vehicleOptions.length > 1 && String(vehicleOptions[0].id) === '0') && (
                <Box sx={{ display: 'grid', width: '100%', py: '5px' }}>
                    <CmLabel htmlFor="vehicle">{t('update-tour-step2-vehicle')}</CmLabel>
                    <Select
                        labelId="vehicle-id"
                        name="vehicle"
                        value={vehicleKey}
                        onChange={handleChangeVehicle}
                        disabled={!hasVehicleExtras}
                        MenuProps={MenuProps}
                        sx={{ fontSize: '13px', height: '40px', borderRadius: '8px' }}
                    >
                        {vehicleOptions.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.vehicle_type} {item.price ? `- $${item.price}` : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {(() => {
                const selectedVehicle = vehicleOptions.find(v => v.id === vehicleKey) || null;
                const baseOk = vehicleOptions.length > 1 && String(vehicleOptions[0]?.id) === '0' && selectedVehicle != null;
                if (!baseOk) return null;

                const selectedFromList = vehicleOptions.find(v => String(v.id) === String(vehicleKey)) || null;
                if (!selectedFromList || !('vehicle_option' in selectedFromList) || !selectedFromList.vehicle_option) {
                    return null;
                }

                const vo = Array.isArray(selectedFromList.vehicle_option)
                    ? selectedFromList.vehicle_option[0]
                    : selectedFromList.vehicle_option;

                if (!vo) return null;

                const label = `${vo?.name ?? t('update-tour-step2-option-fallback')}${vo?.price ? ` - $${vo.price}` : ''}`;

                return (
                    <Box sx={{ pb: '10px' }}>
                        <Box>
                            <CmLabel htmlFor="complements">{t('update-tour-step2-complements')}</CmLabel>
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={vehicleOptionChecked}
                                    onChange={handleToggleVehicleOption}
                                />
                            }
                            label={label}
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                        />
                    </Box>
                );
            })()}

            {/* Seleccion de extras de tour */}
            <Box sx={{ display: 'grid', width: '100%', py: '5px' }}>
                <CmLabel htmlFor="atractions">{t('update-tour-step2-attractions')}</CmLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    {(Array.isArray(extrasAttractions) ? extrasAttractions : []).map((item) => {
                        const name = item?.name || t('update-tour-step2-no-name');
                        const rates = Array.isArray(item?.rates) ? item.rates : [];
                        const visibleRates = rates.filter((rate) => rateMatchesSelectedPax(rate, selectedPaxTypes));
                        const svg = item?.icon || null;
                        const itemKey = getAttractionKey(item);      // ← key normalizada
                        const checked = selectedAttractions.includes(itemKey);
                        const rateQtyMap = attractionRateQuantities?.[itemKey] || {};

                        const toggle = () => toggleAttraction(itemKey);
                        const onKeyDown = (e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault();
                                toggle();
                            }
                        };

                        return (
                            <Box
                                key={itemKey}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1,
                                    borderRadius: 1,
                                    border: '1px solid #eee',
                                    cursor: 'pointer',
                                    backgroundColor: checked ? '#f5f5f5' : 'transparent',
                                    transition: 'background 0.2s',
                                }}
                                onClick={toggle}
                                role="checkbox"
                                aria-checked={checked}
                                tabIndex={0}
                                onKeyDown={onKeyDown}
                            >
                                <Box
                                    sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: 13, color: 'secondary.main' }}>{name}</Typography>
                                    {visibleRates.length > 0 ? (
                                        visibleRates.map((rate, rIdx) => {
                                            const rateKey = toRateKey(rate, rIdx);
                                            const rateMax = getRateMaxQty(rate, persons);
                                            const rateQty = Math.max(0, Math.min(rateMax, Number(rateQtyMap?.[rateKey]) || 0));
                                            const token = `${itemKey}::${rateKey}`;
                                            const paxLabel = rate?.pax != null
                                                ? String(rate.pax)
                                                : t('update-tour-step2-no-name');
                                            const ratePrice = Number(rate?.price) || 0;
                                            return (
                                                <Box
                                                    key={`${itemKey}-rate-${rIdx}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    {/* <Typography sx={{ fontSize: 12, color: 'secondary.main' }}>
                                                        {paxLabel}: {ratePrice ? `$${ratePrice} x ${rateQty}` : t('update-tour-step3-free')}
                                                    </Typography> */}
                                                    {checked && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography sx={{ fontSize: 12, color: 'secondary.main', fontWeight: 500 }}>
                                                                {paxLabel}: {resolvedCurrencySymbol}{ratePrice}
                                                            </Typography>
                                                            <IncrementalButtons
                                                                option={{ title: token, value: rateQty, min_pax: 0 }}
                                                                onIncrement={handleRateIncrement}
                                                                onDecrement={handleRateDecrement}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })
                                    ) : (
                                        <Typography sx={{ fontSize: 12, color: 'secondary.main' }}>
                                            {t('update-tour-step3-free')}
                                        </Typography>
                                    )}
                                </Box>
                                <Checkbox
                                    checked={checked}
                                    onChange={(e) => { e.stopPropagation(); toggle(); }}
                                    onClick={(e) => e.stopPropagation()}
                                    inputProps={{ 'aria-label': t('update-tour-step2-select-item', { name }) }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </>
    );
};

export default Step2;
